import React, { useCallback, useRef, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { useLuminaAnalyticsEvents } from '../../analytics/use-lumina-analytics-events';
import { useDelayedResizeObserver } from '../../hooks/use-delayed-resize-observer';
import { useFetchSmartLinkData } from '../../hooks/use-fetch-smart-link-data';
import { CardVariant, KnowledgeSmartCard } from '../../knowledge-smart-card/knowledge-smart-card';
import type { KnowledgeCardSource } from '../../knowledge-smart-card/types';
import { AnimatedContainer } from '../animated/animated-container';
import { useLuminaInteraction } from '../interaction-context';
import type { SmartLinkElementType } from '../types';

/** Width threshold at or below which COMPACT variant is used when self-determining */
const COMPACT_WIDTH_THRESHOLD = 480;

interface SmartLinkComponentProps {
	component: SmartLinkElementType;
	containerType?: 'carousel';
	indexInContainer?: number;
	isStreaming?: boolean;
	sectionKey?: string;
	/** Optional variant override. When provided, skips width-based self-determination. */
	variant?: CardVariant;
}

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
});

/**
 * Convert SmartLinkElement to KnowledgeCardSource format
 */
function convertToKnowledgeCardSource(
	component: SmartLinkElementType,
	resolvedData?: any,
): KnowledgeCardSource {
	const url = component.attributes?.url || '';
	const title = component.attributes?.title;

	return {
		id: resolvedData?.['@id'] || url,
		title: title || resolvedData?.name || url,
		url,
		product: resolvedData?.generator?.name || null,
		type: resolvedData?.['@type'] || 'link',
		iconUrl: resolvedData?.generator?.icon?.url || null,
		lastModified: resolvedData?.dateModified || null,
		spaceName: null,
		spaceUrl: null,
		ari: resolvedData?.ari,
		text: component.attributes?.text,
	};
}

/**
 * Compute the card variant based on container width.
 * Returns COMPACT at or below the threshold, FULL above.
 */
function computeVariantFromWidth(isNarrowWidth: boolean): CardVariant {
	return isNarrowWidth ? CardVariant.COMPACT : CardVariant.FULL;
}

/**
 * SmartLinkElement renders a smart link card with metadata fetched via smart link resolution.
 *
 * Variant determination:
 * 1. If `variant` prop is provided, use it (allows parent override)
 * 2. If component is an action card, use ACTION_CARD variant
 * 3. Otherwise, self-determine based on container width (COMPACT ≤ 480px, FULL > 480px)
 */
export const SmartLinkElement = ({
	component,
	variant,
	isStreaming = false,
	sectionKey,
	containerType,
	indexInContainer,
}: SmartLinkComponentProps): React.JSX.Element | null => {
	const { fireEvent } = useLuminaAnalyticsEvents();
	const onInteraction = useLuminaInteraction();
	const containerRef = useRef<HTMLDivElement>(null);
	const [isNarrowWidth, setIsNarrowWidth] = useState(false);

	const handleResize = useCallback<ResizeObserverCallback>((entries) => {
		const entry = entries[0];
		if (entry) {
			const isNarrow = entry.contentRect.width <= COMPACT_WIDTH_THRESHOLD;
			setIsNarrowWidth((prev) => (prev !== isNarrow ? isNarrow : prev));
		}
	}, []);

	useDelayedResizeObserver(containerRef, handleResize);

	const url = component.attributes?.url || '';
	const isActionCard = component.attributes?.type === 'action';

	// Determine the effective variant
	// 1. Explicit variant prop takes precedence (parent override)
	// 2. Action cards use ACTION_CARD variant
	// 3. Otherwise, self-determine based on width
	let effectiveVariant: CardVariant;
	if (variant) {
		effectiveVariant = variant;
	} else if (isActionCard) {
		effectiveVariant = CardVariant.ACTION_CARD;
	} else {
		// Self-determine variant based on container width
		effectiveVariant = computeVariantFromWidth(isNarrowWidth);
	}

	// Track whether we've already reported a resolution error for this component instance
	const reportedErrorRef = useRef(false);

	const { objectResolverData } = useFetchSmartLinkData({ urls: [url] });
	const resolvedData = objectResolverData?.[0];

	const knowledgeCardSource = convertToKnowledgeCardSource(component, resolvedData);

	const handleClick = useCallback(
		({ isContextMenu }: { isContextMenu?: boolean }) => {
			onInteraction?.({
				type: isContextMenu ? 'cardContextMenuOpened' : 'cardActivated',
				component: isActionCard ? 'actionCard' : 'smartLink',
			});
			fireEvent('ui.lumina.interacted', {
				componentType: 'smartLink',
				interactionType: 'clicked',
				product: knowledgeCardSource.product,
				cardVariant: effectiveVariant ?? null,
				sectionKey: sectionKey ?? null,
				containerType: containerType ?? null,
				indexInContainer: indexInContainer ?? null,
			});
		},
		[
			onInteraction,
			isActionCard,
			fireEvent,
			knowledgeCardSource.product,
			effectiveVariant,
			sectionKey,
			containerType,
			indexInContainer,
		],
	);

	const handleError = useCallback(
		// eslint-disable-next-line no-unused-vars
		(_error: any) => {
			if (!reportedErrorRef.current) {
				reportedErrorRef.current = true;
				fireEvent('operational.lumina.errored', {
					errorType: 'smartLinkResolution',
					context: sectionKey ?? null,
				});
			}
		},
		[fireEvent, sectionKey],
	);

	return (
		<AnimatedContainer isStreaming={isStreaming} fillHeight={containerType === 'carousel'}>
			<Box
				ref={containerRef}
				xcss={containerType === 'carousel' ? styles.fullHeight : undefined}
				testId="lumina-layout-smart-link"
			>
				<KnowledgeSmartCard
					key={url}
					source={knowledgeCardSource}
					variant={effectiveVariant}
					onClickHandler={handleClick}
					onError={handleError}
				/>
			</Box>
		</AnimatedContainer>
	);
};
